import React, { useState, useEffect, useMemo, useRef } from "react";
import HelpButton from "../components/shared/HelpButton";
import { cssUrl } from "../utils/cssVersion";

/**
 * ActiveReplacements — read-only twin in the OnboardNonActive style.
 *
 *   · five full-width total buttons, none lit by default, one at a time,
 *     click the lit button again to clear the table
 *   · Contractor Available / Employee Available — every name from the
 *     "available" list; names with shift rows show one row per shift,
 *     names without show "No Site" and 0 hours
 *   · Contractor Shifts / Employee Shifts — every shift row as-is
 *   · Current Replacements — existing replacement records with the day
 *     flags summarised as Mon–Fri / Sat–Sun
 *   · search narrows the totals and the table (Cleaner, Site, Owner, Replaced —
 *     Current Replacements also matches the record's Site even though it isn't shown)
 *   · ↻ refresh keeps the lit button and the search
 *   · rows are read-only — nothing opens on click
 *
 * Data (existing routes, used as-is):
 *   /api/replacement/abn-shifts  → { names, all }  all row: [id, subbie, site, shiftHrs, mon..sun, daystatus]
 *   /api/replacement/tfn-shifts  → { names, all }  all row: [id, employee, site, shiftHrs, mon..sun, daystatus]
 *   /api/replacement/abn-names   → { names, all }  names = available ABN cleaners
 *   /api/replacement/existing    → { all }         row: [id, start, end, site, hrs, replacedStatus,
 *                                                    replacedByStatus, cleanerReplaced, replacementCleaner,
 *                                                    one..seven (idx 9–15), ownerShiftName (idx 16)]
 */

/* ---------- constants ---------- */
const DAY_NAMES = ["Mon", "Tues", "Wed", "Thur", "Fri", "Sat", "Sun"];
const SHIFT_DAY_OFFSET = 4;   // shift rows: Mon..Sun at idx 4–10
const REP_DAY_OFFSET = 9;     // replacement rows: one..seven at idx 9–15

const VIEWS = [
  { key: "abnAvail",  label: "Contractor Available",  color: "#4ade80", kind: "shift" },
  { key: "abnShifts", label: "Contractor Shifts",     color: "#fbbf24", kind: "shift" },
  { key: "tfnAvail",  label: "Employee Available",    color: "#22d3ee", kind: "shift" },
  { key: "tfnShifts", label: "Employee Shifts",       color: "#a78bfa", kind: "shift" },
  { key: "current",   label: "Current Replacements",  color: "#f87171", kind: "rep"   },
];

const SHIFT_HEADERS = ["Cleaner", "Site", ...DAY_NAMES];
const SHIFT_COLS = "minmax(150px,1.6fr) minmax(140px,1.4fr) repeat(7, minmax(52px,0.55fr))";

const REP_HEADERS = ["Start Date", "End Date", "Owner Type", "Owner", "Replaced Type", "Replaced", "Mon–Fri", "Sat–Sun"];
const REP_COLS = "repeat(2, minmax(92px,0.8fr)) minmax(80px,0.6fr) minmax(140px,1.4fr) minmax(90px,0.6fr) minmax(140px,1.4fr) minmax(120px,1.1fr) minmax(80px,0.7fr)";

/* ---------- helpers ---------- */
const displayDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = String(iso).split("-");
  return d && m && y ? `${d}/${m}/${y}` : iso;
};

const fmtHours = (n) => {
  const v = Number(n) || 0;
  return Number.isInteger(v) ? String(v) : v.toFixed(1);
};

// shift row → table row
const shiftRow = (r, key) => ({
  key,
  cleaner: r[1] || "",
  site: r[2] || "No Site",
  days: DAY_NAMES.map((_, i) => Number(r[SHIFT_DAY_OFFSET + i]) || 0),
});

// name with no shifts → placeholder row
const emptyShiftRow = (name, key) => ({
  key,
  cleaner: name,
  site: "No Site",
  days: [0, 0, 0, 0, 0, 0, 0],
});

// "available" list: one row per shift if the name has shifts, else a No Site row
const buildAvailable = (names, shiftData, prefix) => {
  const byName = new Map();
  for (const r of shiftData) {
    if (!r[1]) continue;
    if (!byName.has(r[1])) byName.set(r[1], []);
    byName.get(r[1]).push(r);
  }
  const rows = [];
  for (const name of names) {
    const shifts = byName.get(name);
    if (shifts && shifts.length) {
      shifts.forEach((r, i) => rows.push(shiftRow(r, `${prefix}-${r[0] || name}-${i}`)));
    } else {
      rows.push(emptyShiftRow(name, `${prefix}-${name}`));
    }
  }
  return rows;
};

const buildShifts = (shiftData, prefix) =>
  shiftData.filter((r) => r[1]).map((r, i) => shiftRow(r, `${prefix}-${r[0] || i}`));

// day flags 1/0 → "Mon, Tue, Fri"
const summariseDays = (r, from, to) => {
  const out = [];
  for (let i = from; i <= to; i++) {
    if (Number(r[REP_DAY_OFFSET + i]) === 1) out.push(DAY_NAMES[i]);
  }
  return out.length ? out.join(", ") : "—";
};

const buildReplacements = (existing) =>
  existing.map((r, i) => ({
    key: `rep-${r[0] || i}`,
    start: r[1] || "",
    end: r[2] || "",
    site: r[3] || "",          // not displayed — used by search only
    ownerType: r[5] || "",
    owner: r[16] || "",
    replacedType: r[6] || "",
    replaced: r[7] || "",
    monFri: summariseDays(r, 0, 4),
    satSun: summariseDays(r, 5, 6),
  }));

const sortByName = (rows, field) =>
  [...rows].sort((a, b) => String(a[field]).localeCompare(String(b[field]), undefined, { sensitivity: "base" }));

/* ---------- styles ---------- */
const S = {
  panel: { padding: "18px 20px 16px", width: "100%", maxWidth: "none", margin: 0, boxSizing: "border-box" },
  header: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 12 },
  title: { margin: 0, fontSize: "1.05rem", fontWeight: 700, color: "#e5e7eb" },
  right: { display: "flex", alignItems: "center", gap: 10 },
  refresh: {
    width: 32, height: 32, borderRadius: 8, border: "1px solid rgba(255,255,255,0.15)",
    background: "rgba(255,255,255,0.04)", color: "#e5e7eb", cursor: "pointer",
    display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 16,
  },
  search: { marginBottom: 12 },
  clear: { background: "none", border: "none", color: "#22d3ee", cursor: "pointer", fontSize: "0.78rem", padding: "4px 0" },
  buttons: { display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: 8, marginBottom: 14 },
  btn: (c, lit) => ({
    padding: "12px 8px", borderRadius: 10, cursor: "pointer",
    border: `1px solid ${lit ? c : "rgba(255,255,255,0.12)"}`,
    background: lit ? `${c}22` : "rgba(255,255,255,0.03)",
    color: lit ? c : "rgba(255,255,255,0.65)",
    display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
    transition: "all .15s ease", minWidth: 0,
  }),
  btnTotal: { fontSize: "1.35rem", fontWeight: 800, lineHeight: 1 },
  btnLabel: { fontSize: "0.72rem", fontWeight: 600, textAlign: "center", lineHeight: 1.2 },
  tableWrap: { overflowX: "auto", borderRadius: 10, border: "1px solid rgba(255,255,255,0.08)" },
  tableHead: { display: "flex", alignItems: "center", justifyContent: "space-between", margin: "4px 2px 8px" },
  listName: (c) => ({ color: c, fontWeight: 700, fontSize: "0.9rem" }),
  count: { color: "rgba(255,255,255,0.45)", fontSize: "0.78rem" },
  headRow: (cols) => ({
    display: "grid", gridTemplateColumns: cols, gap: 8, padding: "8px 12px",
    background: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.55)",
    fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em",
    position: "sticky", top: 0,
  }),
  row: (cols) => ({
    display: "grid", gridTemplateColumns: cols, gap: 8, padding: "9px 12px",
    borderTop: "1px solid rgba(255,255,255,0.06)", color: "#e5e7eb",
    fontSize: "0.82rem", alignItems: "center", cursor: "default",
  }),
  cell: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  num: (v) => ({ textAlign: "center", color: v > 0 ? "#22d3ee" : "rgba(255,255,255,0.3)", fontVariantNumeric: "tabular-nums" }),
  muted: { color: "rgba(255,255,255,0.35)", fontStyle: "italic" },
  empty: { padding: "26px 12px", textAlign: "center", color: "rgba(255,255,255,0.4)", fontSize: "0.85rem" },
  hint: { color: "rgba(255,255,255,0.35)", fontSize: "0.74rem", marginTop: 8 },
  loading: { display: "flex", alignItems: "center", justifyContent: "center", minHeight: 220 },
  error: { color: "#f87171", fontSize: "0.85rem", padding: "12px 2px" },
};

/* ---------- component ---------- */
const ActiveReplacements = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [abnShiftData, setAbnShiftData] = useState([]);
  const [abnSingleNames, setAbnSingleNames] = useState([]);
  const [tfnShiftData, setTfnShiftData] = useState([]);
  const [tfnNames, setTfnNames] = useState([]);
  const [existing, setExisting] = useState([]);

  const [active, setActive] = useState(null);   // VIEWS key or null
  const [search, setSearch] = useState("");
  const fetchedRef = useRef(false);

  /* ---------- stylesheet (ob-container / ob-form-card / ob-input) ---------- */
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = cssUrl("newuseronboarding.css");
    document.head.appendChild(link);
    return () => { document.head.removeChild(link); };
  }, []);

  /* ---------- data ---------- */
  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [tfnRes, abnRes, abnNamesRes, existingRes] = await Promise.all([
        fetch("/api/replacement/tfn-shifts"),
        fetch("/api/replacement/abn-shifts"),
        fetch("/api/replacement/abn-names"),
        fetch("/api/replacement/existing"),
      ]);
      const [tfnData, abnData, abnNamesData, existingData] = await Promise.all([
        tfnRes.json(), abnRes.json(), abnNamesRes.json(), existingRes.json(),
      ]);
      setTfnShiftData(tfnData.all || []);
      setTfnNames(tfnData.names || []);
      setAbnShiftData(abnData.all || []);
      setAbnSingleNames(abnNamesData.names || []);
      setExisting(existingData.all || []);
    } catch (err) {
      console.error("Failed to load replacement data:", err);
      setError("Couldn't load replacement data. Use ↻ to try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    load();
  }, []);

  /* ---------- build the five lists ---------- */
  const lists = useMemo(() => ({
    abnAvail:  sortByName(buildAvailable(abnSingleNames, abnShiftData, "abn-av"), "cleaner"),
    abnShifts: sortByName(buildShifts(abnShiftData, "abn-sh"), "cleaner"),
    tfnAvail:  sortByName(buildAvailable(tfnNames, tfnShiftData, "tfn-av"), "cleaner"),
    tfnShifts: sortByName(buildShifts(tfnShiftData, "tfn-sh"), "cleaner"),
    current:   [...buildReplacements(existing)].sort((a, b) => String(b.start).localeCompare(String(a.start))),
  }), [abnSingleNames, abnShiftData, tfnNames, tfnShiftData, existing]);

  /* ---------- search narrows every list ---------- */
  const q = search.trim().toLowerCase();
  const matches = (v) => String(v || "").toLowerCase().includes(q);

  const filtered = useMemo(() => {
    if (!q) return lists;
    return {
      abnAvail:  lists.abnAvail.filter((r) => matches(r.cleaner) || matches(r.site)),
      abnShifts: lists.abnShifts.filter((r) => matches(r.cleaner) || matches(r.site)),
      tfnAvail:  lists.tfnAvail.filter((r) => matches(r.cleaner) || matches(r.site)),
      tfnShifts: lists.tfnShifts.filter((r) => matches(r.cleaner) || matches(r.site)),
      current:   lists.current.filter((r) => matches(r.owner) || matches(r.replaced) || matches(r.site)),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lists, q]);

  const view = VIEWS.find((v) => v.key === active) || null;
  const rows = view ? filtered[view.key] : [];

  /* ---------- render ---------- */
  return (
    <div className="ob-container" style={{ padding: 0, width: "100%", maxWidth: "none", margin: 0 }}>
      <style>{`
        .arp-row{transition:background .12s ease}.arp-row:hover{background:rgba(34,211,238,0.04)}
        .arp-btn:focus-visible{outline:2px solid #22d3ee;outline-offset:2px}
        @keyframes arp-spin{to{transform:rotate(360deg)}}
        @media (max-width:760px){.arp-buttons{grid-template-columns:repeat(2,minmax(0,1fr))!important}}`}</style>

      <div className="ob-form-card" style={S.panel}>
        {/* header */}
        <div style={S.header}>
          <h4 style={S.title}>Replacements — View Only</h4>
          <div style={S.right}>
            <HelpButton helpData={[{ chip: "activereplacements", urlFilter: "/static/pdffilters/ActiveReplacements.pdf" }]} />
            <button type="button" style={S.refresh} onClick={load} title="Refresh" aria-label="Refresh">↻</button>
          </div>
        </div>

        {/* search */}
        <div style={S.search}>
          <input
            type="text"
            className="ob-input"
            placeholder="Search cleaner, site, owner…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button type="button" style={S.clear} onClick={() => setSearch("")}>Clear search</button>
          )}
        </div>

        {/* five totals */}
        <div className="arp-buttons" style={S.buttons}>
          {VIEWS.map((v) => {
            const lit = active === v.key;
            return (
              <button
                key={v.key}
                type="button"
                className="arp-btn"
                style={S.btn(v.color, lit)}
                onClick={() => setActive(lit ? null : v.key)}
                aria-pressed={lit}
              >
                <span style={S.btnTotal}>{loading ? "…" : filtered[v.key].length}</span>
                <span style={S.btnLabel}>{v.label}</span>
              </button>
            );
          })}
        </div>

        {error && <div style={S.error}>{error}</div>}

        {/* loading */}
        {loading && (
          <div style={S.loading}>
            <div className="fr-loading-state">
              <div className="fr-spinner" style={{ animation: "arp-spin 1s linear infinite" }} />
              <div className="fr-loading-text">Loading replacements…</div>
            </div>
          </div>
        )}

        {/* table */}
        {!loading && view && (
          <>
            <div style={S.tableHead}>
              <span style={S.listName(view.color)}>{view.label}</span>
              <span style={S.count}>{rows.length} record{rows.length === 1 ? "" : "s"}</span>
            </div>

            <div style={S.tableWrap}>
              {view.kind === "shift" ? (
                <>
                  <div style={S.headRow(SHIFT_COLS)}>
                    {SHIFT_HEADERS.map((h, i) => (
                      <span key={h} style={i >= 2 ? { textAlign: "center" } : undefined}>{h}</span>
                    ))}
                  </div>
                  {rows.length === 0 && <div style={S.empty}>No records match.</div>}
                  {rows.map((r) => (
                    <div key={r.key} className="arp-row" style={S.row(SHIFT_COLS)}>
                      <span style={S.cell} title={r.cleaner}>{r.cleaner}</span>
                      <span style={{ ...S.cell, ...(r.site === "No Site" ? S.muted : {}) }} title={r.site}>{r.site}</span>
                      {r.days.map((h, i) => (
                        <span key={i} style={S.num(h)}>{fmtHours(h)}</span>
                      ))}
                    </div>
                  ))}
                </>
              ) : (
                <>
                  <div style={S.headRow(REP_COLS)}>
                    {REP_HEADERS.map((h) => <span key={h}>{h}</span>)}
                  </div>
                  {rows.length === 0 && <div style={S.empty}>No current replacements.</div>}
                  {rows.map((r) => (
                    <div key={r.key} className="arp-row" style={S.row(REP_COLS)}>
                      <span style={S.cell}>{displayDate(r.start)}</span>
                      <span style={S.cell}>{displayDate(r.end)}</span>
                      <span style={S.cell}>{r.ownerType}</span>
                      <span style={S.cell} title={r.owner}>{r.owner}</span>
                      <span style={S.cell}>{r.replacedType}</span>
                      <span style={S.cell} title={r.replaced}>{r.replaced}</span>
                      <span style={{ ...S.cell, color: r.monFri === "—" ? "rgba(255,255,255,0.3)" : "#22d3ee" }}>{r.monFri}</span>
                      <span style={{ ...S.cell, color: r.satSun === "—" ? "rgba(255,255,255,0.3)" : "#22d3ee" }}>{r.satSun}</span>
                    </div>
                  ))}
                </>
              )}
            </div>

            <div style={S.hint}>Read-only view — records can't be edited here.</div>
          </>
        )}

        {!loading && !view && !error && (
          <div style={S.hint}>Click a total above to list its records.</div>
        )}
      </div>
    </div>
  );
};

export default ActiveReplacements;